import {
  DEFAULT_REPORT_SORTS,
  reportSortsSchema,
  type ReportSort,
} from "@meta-video/contracts";

const SORT_PARAM = "sort";
const ENTRY_SEPARATOR = ",";
const FIELD_SEPARATOR = ":";

function isDefault(sorts: readonly ReportSort[]): boolean {
  return sorts.length === DEFAULT_REPORT_SORTS.length
    && sorts.every((sort, index) =>
      sort.metric === DEFAULT_REPORT_SORTS[index]!.metric
      && sort.direction === DEFAULT_REPORT_SORTS[index]!.direction);
}

/**
 * The report sort is a request input, so it belongs in the URL beside the
 * filters and the video selection. Holding it only in component state meant
 * any remount silently reverted the user to the default spend ordering while
 * their column choices, which are persisted, survived.
 *
 * An unreadable value is not an error worth surfacing: a shared or hand-edited
 * link simply falls back to the default ordering.
 */
export function parseReportSortSearch(search: string): readonly ReportSort[] {
  const raw = new URLSearchParams(search).get(SORT_PARAM);
  if (raw === null || raw === "") return DEFAULT_REPORT_SORTS;
  const entries = raw.split(ENTRY_SEPARATOR).map((entry) => {
    const [metric, direction] = entry.split(FIELD_SEPARATOR);
    return { direction, metric };
  });
  const parsed = reportSortsSchema.safeParse(entries);
  return parsed.success ? parsed.data : DEFAULT_REPORT_SORTS;
}

/**
 * Writes the sort into `existing`, preserving every other parameter. The
 * default ordering is represented by the absence of the key so an untouched
 * report keeps a clean URL.
 */
export function serializeReportSortSearch(
  sorts: readonly ReportSort[],
  existing: string | URLSearchParams = "",
): string {
  const params = new URLSearchParams(existing);
  if (isDefault(sorts) || reportSortsSchema.safeParse(sorts).success === false) {
    params.delete(SORT_PARAM);
  } else {
    params.set(
      SORT_PARAM,
      sorts
        .map(({ direction, metric }) => `${metric}${FIELD_SEPARATOR}${direction}`)
        .join(ENTRY_SEPARATOR),
    );
  }
  return params.toString();
}
